import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { Bell, CalendarDays, Send } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, Panel, PanelLabel } from "@/components/PageHeader";
import { api, formatApiError } from "@/lib/api";
import { DOLLAR_IMG, DollarAvatar, useDollar, type DollarStage, type DollarState } from "@/lib/dollar";

interface Msg { id: string; role: "user" | "assistant"; message: string; kind?: string | null; week?: string | null }
interface MentorState {
  messages: Msg[]; current_week: string; remaining: number; monthly_limit: number; max_chars: number; available: boolean;
  today: { month_progress: number; plan_spent: number | null; plan_spent_open: number | null; on_track: number; total: number };
  rules: string[]; alerts: { id: string; title: string; body: string }[];
}
const SUGGESTED = ["¿Cuánto puedo gastar al día?", "¿En qué se me fue más?", "¿Me alcanza para…?"];
const pct = (v: number | null) => (v === null ? "—" : `${Math.round(v * 100)}%`);

const EMPTY_TEXT: Record<DollarStage, string> = {
  puppy: "Hola, soy Dollar. Vamos aprendiendo juntos: arma tu presupuesto y registra tus movimientos, y yo voy creciendo contigo.",
  adult: "Hola, soy Dollar. Ya conozco tus números; pregúntame cómo vas con tu dinero.",
};

function DollarEmpty({ d }: { d: DollarState }) {
  const p = d.progress;
  return (
    <div className="flex flex-col items-center py-6 text-center" data-testid="mentor-empty">
      <img src={DOLLAR_IMG[d.stage].full} alt="Dollar" className="h-48 w-auto sm:h-60" data-testid={`dollar-hero-${d.stage}`} />
      <p className="mt-4 max-w-md text-base text-[#1A1F2B]" data-testid="mentor-empty-text">{EMPTY_TEXT[d.stage]}</p>
      {d.stage === "puppy" && (
        <div className="mt-4 max-w-md text-[13px] text-[#5A6170]" data-testid="dollar-progress">
          <p>Para que Dollar crezca, cumple estas tres al mismo tiempo:</p>
          <p className="mt-1 font-semibold text-[#1B2A4A]">
            Movimientos: {p.movements_months} de {p.needed} meses · Presupuesto: {p.budget_ok ? "listo" : "pendiente"} · Ahorro: {p.savings_months} de {p.needed} meses
          </p>
          <p className="mt-1">Registra al menos {p.min_moves} movimientos cada mes y aparta a tu ahorro más de lo que sacas. ¡Vas bien!</p>
        </div>
      )}
    </div>
  );
}

function Bubble({ m, stage }: { m: Msg; stage: DollarStage }) {
  const mine = m.role === "user";
  if (m.kind === "weekly") {
    return (
      <div className="rounded-2xl border border-[#C9A84C]/50 bg-amber-50/60 px-4 py-3" data-testid="mentor-msg-weekly">
        <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#2C4F91]"><CalendarDays className="h-3.5 w-3.5" /> Lectura de la semana</p>
        <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-[#1B2A4A]">{m.message.replace(/\*\*/g, "")}</p>
      </div>
    );
  }
  return (
    <div className={`flex items-end gap-2 ${mine ? "justify-end" : "justify-start"}`} data-testid={`mentor-msg-${m.role}`}>
      {!mine && <DollarAvatar stage={stage} />}
      <p className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed ${mine ? "rounded-br-sm bg-[#1B2A4A] text-white" : "rounded-bl-sm border border-[#DDE1E8] bg-white text-[#1B2A4A]"}`}>{m.message.replace(/\*\*/g, "")}</p>
    </div>
  );
}

function SidePanels({ s }: { s: MentorState }) {
  const rows: [string, string][] = [["Avance del mes", pct(s.today.month_progress)], ["Plan gastado", pct(s.today.plan_spent)],
    ["Sin contar lo ya cubierto", pct(s.today.plan_spent_open)], ["Categorías en ritmo", `${s.today.on_track} de ${s.today.total}`]];
  return (
    <div className="space-y-6">
      <Panel testId="mentor-today">
        <PanelLabel>Cómo vas hoy</PanelLabel>
        <ul className="mt-3 space-y-2">{rows.map(([l, v]) => <li key={l} className="flex justify-between text-sm"><span className="text-slate-500">{l}</span><b className="tabular text-[#1B2A4A]">{v}</b></li>)}</ul>
      </Panel>
      {s.alerts.length > 0 && (
        <Panel testId="mentor-alerts">
          <PanelLabel>Tus avisos</PanelLabel>
          <ul className="mt-3 space-y-3">{s.alerts.map((a) => <li key={a.id} className="text-sm"><p className="font-semibold text-[#1B2A4A]">{a.title}</p><p className="text-slate-500">{a.body}</p></li>)}</ul>
        </Panel>
      )}
      <Panel testId="mentor-rules">
        <PanelLabel>Cuándo te aviso</PanelLabel>
        <ul className="mt-3 space-y-2">{s.rules.map((r) => <li key={r} className="flex gap-2 text-sm text-slate-600"><Bell className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />{r}</li>)}</ul>
      </Panel>
    </div>
  );
}

export default function MentorPage() {
  const [s, setS] = useState<MentorState | null>(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const { state: dollar } = useDollar();
  const stage: DollarStage = dollar?.stage ?? "puppy";
  const end = useRef<HTMLDivElement>(null);
  const load = useCallback(() => {
    api.post("/mentor/weekly").catch(() => undefined)
      .then(() => api.get<MentorState>("/mentor")).then((r) => setS(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(load, [load]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [s?.messages.length, busy]);

  const send = async (text: string) => {
    const question = text.trim();
    if (!question || busy || !s) return;
    setBusy(true);
    setS({ ...s, messages: [...s.messages, { id: `tmp-${Date.now()}`, role: "user", message: question }] });
    setQ("");
    try {
      const r = await api.post<{ answer: string; remaining: number }>("/mentor/ask", { question });
      setS((p) => p && { ...p, remaining: r.data.remaining, messages: [...p.messages, { id: `a-${Date.now()}`, role: "assistant", message: r.data.answer }] });
    } catch (e) {
      toast.error(formatApiError(e));
      setS((p) => p && { ...p, messages: p.messages.filter((m) => !m.id.startsWith("tmp-") || m.message !== question) });
      setQ(question);
    }
    setBusy(false);
  };
  const submit = (e: FormEvent) => { e.preventDefault(); send(q); };

  const pinned = s?.messages.find((m) => m.kind === "weekly" && m.week === s.current_week);
  const rest = s?.messages.filter((m) => m !== pinned) ?? [];

  return (
    <section data-testid="mentor-page">
      <PageHeader title="Mentor" testId="mentor-title" />
      {!s ? <div className="h-64 animate-pulse rounded-[14px] bg-white" /> : (
        <div className="fade-up grid gap-6 lg:grid-cols-[1.8fr_1fr]">
          <Panel className="flex flex-col" testId="mentor-chat">
            {dollar?.stage === "puppy" && s.messages.some((m) => m.kind !== "weekly") && (
              <div className="mb-3 flex items-center gap-3 rounded-xl bg-[#F4EFE6] px-3 py-2 text-[13px] text-[#1B2A4A]" data-testid="dollar-progress-strip">
                <DollarAvatar stage="puppy" className="h-8 w-8" />
                <span>Para que Dollar crezca: <b>Movimientos: {dollar.progress.movements_months} de {dollar.progress.needed} meses · Presupuesto: {dollar.progress.budget_ok ? "listo" : "pendiente"} · Ahorro: {dollar.progress.savings_months} de {dollar.progress.needed} meses</b></span>
              </div>
            )}
            <div className="max-h-[60vh] min-h-[320px] space-y-3 overflow-y-auto pr-1" data-testid="mentor-messages">
              {pinned && <Bubble m={pinned} stage={stage} />}
              {!s.messages.some((m) => m.kind !== "weekly") && !busy && dollar && <DollarEmpty d={dollar} />}
              {rest.map((m) => <Bubble key={m.id} m={m} stage={stage} />)}
              {busy && <p className="flex items-center gap-2 text-[13px] text-[#5A6170]" data-testid="mentor-thinking"><DollarAvatar stage={stage} className="h-8 w-8 animate-pulse" /> Dollar está pensando…</p>}
              <div ref={end} />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {SUGGESTED.map((t) => <button key={t} type="button" disabled={busy} onClick={() => (t.endsWith("…?") ? setQ("¿Me alcanza para ") : send(t))} className="rounded-full border border-dashed border-[#C9A84C] px-3 py-1 text-xs font-medium text-[#1B2A4A] transition-colors hover:bg-amber-50 disabled:opacity-50" data-testid={`mentor-suggest-${SUGGESTED.indexOf(t)}`}>{t}</button>)}
            </div>
            <form onSubmit={submit} className="mt-3 flex items-end gap-2">
              <Textarea value={q} maxLength={s.max_chars} rows={2} placeholder="Escribe tu pregunta…" onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setQ(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(q); } }} className="resize-none" data-testid="mentor-input" />
              <button type="submit" disabled={busy || !q.trim()} aria-label="Enviar" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-[#1B2A4A] text-white transition-colors hover:bg-[#2A3D63] disabled:opacity-40" data-testid="mentor-send"><Send className="h-4 w-4" /></button>
            </form>
            <p className="mt-2 flex justify-between text-xs text-slate-500">
              <span data-testid="mentor-remaining">Te quedan {s.remaining} de {s.monthly_limit} este mes</span><span>{q.length}/{s.max_chars}</span>
            </p>
            <p className="mt-3 text-[11px] text-slate-400" data-testid="mentor-disclaimer">Tu mentor te orienta con tus propios números. No es asesoría financiera, fiscal ni legal.</p>
          </Panel>
          <SidePanels s={s} />
        </div>
      )}
    </section>
  );
}
